import React from "react";
import {
  SectionContainer,
  SectionTitleHeading,
  HeroBanner,
  HeroBannerRightPanel,
  HeroBannerTitle
} from "@silversea-ssc/ui-components-react";
import { graphql, PageProps } from "gatsby";
import { SubDestinationPageQuery } from "types/graphql-types";

import OpenGraph from "components/SEO/OpenGraph";
import PageBuilderDescriptionText from "components/PageBuilder/PageBuilderDescriptionText";
import Slider, { SliderSlide } from "components/Slider";
import { SectionTitle } from "@silversea-ssc/ui-components-react";
import { DestinationColor } from "@silversea-ssc/ui-components-react";
import ServiceRows from "components/ServiceRow/ServiceRows";

import ServiceRow, {
  ServiceRowContent,
  ServiceRowImages,
  ServiceRowTitle
} from "components/ServiceRow/ServiceRow";
import TravelGuide from "components/destinations/TravelGuide";
import PageBuilderFyc from "../components/PageBuilder/PageBuilderFyc";
import { isPresent } from "src/utils/checks";
import { BlockContent } from "components/BlockContent";
import LinkCard from "components/Card/LinkCard";

const SubDestinationPage: React.FC<PageProps<SubDestinationPageQuery>> = ({
  data,
  location
}) => {
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const page = data.page!;
  const destinationHighlights = page.destinationHighlights?.filter(isPresent);
  const whyRows = page.why?.filter(isPresent);

  return (
    <React.Fragment>
      <OpenGraph
        openGraph={page?.metadata?.openGraph}
        excludeFromSitemap={page?.metadata?.excludeFromSitemap}
      />
      {page.heroImage && (
        <HeroBanner background={page.heroImage}>
          <HeroBannerRightPanel>
            <HeroBannerTitle>{page.title?.localized}</HeroBannerTitle>
          </HeroBannerRightPanel>
        </HeroBanner>
      )}
      <DestinationColor destinationId={page.destination?.destinationId}>
        <SectionContainer spacingTop="regular" spacingBottom="regular">
          <PageBuilderDescriptionText {...page.description} />
        </SectionContainer>
        <SectionContainer
          spacingTop="regular"
          spacingBottom="regular"
          backgroundColor="destinationExtralight"
        >
          <SectionTitle variant="wide">
            <SectionTitleHeading>DESTINATION HIGHLIGHTS</SectionTitleHeading>
          </SectionTitle>

          {destinationHighlights && (
            <Slider
              variantColor="white"
              variant="wide"
              data-content-name="ports_slider"
            >
              {destinationHighlights.map((item) => {
                const portName = item.title?.localized || "";
                const portDescription = item.description?.localized;

                return (
                  <SliderSlide key={item._key}>
                    <LinkCard
                      backgroundColor="white"
                      key={item._key}
                      title={portName}
                      image={item.image}
                      imageRatio="card-panorama"
                      description={portDescription}
                      url={undefined}
                    />
                  </SliderSlide>
                );
              })}
            </Slider>
          )}
        </SectionContainer>

        <PageBuilderFyc {...page.fyc} location={location} />

        <SectionContainer
          spacingTop="regular"
          spacingBottom="regular"
          backgroundColor="white"
        >
          <SectionTitle variant="wide">
            <SectionTitleHeading>
              {page.whyTitle?.localized}
            </SectionTitleHeading>
          </SectionTitle>
          {whyRows && (
            <ServiceRows>
              {whyRows.map((service, index) => {
                const rowImages = service.images?.filter(isPresent);
                return (
                  <ServiceRow
                    key={service._key}
                    variant={index % 2 ? "right" : "left"}
                    backgroundColor="neutral05"
                  >
                    {rowImages && <ServiceRowImages items={rowImages} />}
                    {service.title?.localized && (
                      <ServiceRowTitle tagline={service.tagline?.localized}>
                        {service.title.localized}
                      </ServiceRowTitle>
                    )}
                    {service.description?.localized && (
                      <ServiceRowContent>
                        <BlockContent blocks={service.description.localized} />
                      </ServiceRowContent>
                    )}
                  </ServiceRow>
                );
              })}
            </ServiceRows>
          )}
        </SectionContainer>
        <TravelGuide
          sectionTitle={page.destination?.travelGuideTitle?.localized}
          image={page.destination?.travelGuideImage}
          guideTitle={page.destination?.travelGuideHeader?.localized}
          content={page.destination?.travelGuideBody?.localized}
          url={page.destination?.travelGuideUrl?.localized}
          ctaText={page.destination?.travelGuideButtonText?.localized}
        />
      </DestinationColor>
    </React.Fragment>
  );
};

export const query = graphql`
  query SubDestinationPage($id: String!) {
    page: sanitySubDestination(id: { eq: $id }) {
      metadata {
        excludeFromSitemap
        openGraph {
          ...OpenGraphMetaTags
        }
      }

      name {
        localized
      }

      title {
        localized
      }
      heroImage {
        ...HeroResponsiveImageWithAlt
      }

      description {
        ...PageBuilderDescriptionText
      }

      destination {
        destinationId
        travelGuideBody {
          localized
        }
        travelGuideButtonText {
          localized
        }
        travelGuideHeader {
          localized
        }
        travelGuideQuestion {
          localized
        }
        travelGuideImage {
          ...Image
        }
        travelGuideTitle {
          localized
        }
        travelGuideUrl {
          localized
        }
      }

      destinationHighlights {
        _key
        title {
          localized
        }
        description {
          localized
        }
        image {
          ...ImageWithAltAndPreview
        }
      }

      fyc {
        ...PageBuilderFyc
      }

      whyTitle {
        localized
      }
      why {
        _key
        title {
          localized(fallback: false)
        }

        tagline {
          localized(fallback: false)
        }

        description {
          localized(fallback: false)
        }

        link {
          fullPath
        }

        images {
          __typename
          ... on SanityImageWithAlt {
            ...SlideshowOnlyImageAlt
          }

          ... on SanityVideoWithPosterImage {
            ...VideoWithPosterImage
          }
        }
      }
    }
  }
`;

export default SubDestinationPage;
